/* eslint-disable filenames/match-exported */

// eslint-disable-next-line no-undef
type Key = IDBValidKey;

const stores = {
  LANDING_PAGE_IMAGES: "landing_page_images",
  LANDING_PAGE_INFO: "landing_page_info",
  VIDEO_THUMBNAILS: "video_thumbnails",
  VIDEO_THUMBNAIL_METADATA: "video_thumbnail_metadata"
};

class IndexedDB {
  private dbName: string;

  private version: number;

  private db: IDBDatabase | null;

  private ready: Promise<IDBDatabase>;

  public stores: Record<string, string>;

  constructor() {
    this.dbName = "culturehq";
    this.version = 3;
    this.db = null;
    this.stores = stores;
    this.ready = this.init(Object.values(this.stores));
  }

  async init(stores: string[] = []): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = (e: IDBVersionChangeEvent) => {
        const db = (e.target as IDBOpenDBRequest).result;
        stores.forEach(store => {
          if (!db.objectStoreNames.contains(store)) {
            db.createObjectStore(store);
          }
        });
      };

      request.onsuccess = (e: Event) => {
        this.db = (e.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      request.onerror = (e: Event) => reject((e.target as IDBOpenDBRequest).error);
    });
  }

  async save(store: string, key: Key, value: unknown): Promise<boolean> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readwrite");
      tx.objectStore(store).put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = e => reject((e.target as IDBTransaction).error);
    });
  }

  async get<T = unknown>(store: string, key: Key): Promise<T | undefined> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readonly");
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = e => reject((e.target as IDBRequest).error);
    });
  }

  async delete(store: string, key: Key): Promise<boolean> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readwrite");
      tx.objectStore(store).delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = e => reject((e.target as IDBTransaction).error);
    });
  }

  async getAllKeys(store: string): Promise<Key[]> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readonly");
      const req = tx.objectStore(store).getAllKeys();
      req.onsuccess = () => resolve(req.result);
      req.onerror = e => reject((e.target as IDBRequest).error);
    });
  }

  async getAllByPrefix<T = unknown>(
    store: string,
    prefix: string
  ): Promise<Array<{ key: Key; value: T }>> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readonly");
      const objectStore = tx.objectStore(store);
      const results: Array<{ key: Key; value: T }> = [];
      const range = IDBKeyRange.lowerBound(prefix);
      const req = objectStore.openCursor(range);

      req.onsuccess = (e: Event) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const key = cursor.key as string;
          if (key.startsWith(prefix)) {
            results.push({ key: cursor.key, value: cursor.value as T });
            cursor.continue();
          } else {
            resolve(results);
          }
        } else {
          resolve(results);
        }
      };

      req.onerror = e => reject((e.target as IDBRequest).error);
    });
  }

  async deleteByPrefix(store: string, prefix: string): Promise<number> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readwrite");
      const objectStore = tx.objectStore(store);
      const range = IDBKeyRange.lowerBound(prefix);
      const req = objectStore.openKeyCursor(range);
      let deletedCount = 0;

      req.onsuccess = (e: Event) => {
        const cursor = (e.target as IDBRequest<IDBCursor>).result;
        if (cursor) {
          const key = cursor.key as string;
          if (key.startsWith(prefix)) {
            objectStore.delete(cursor.key);
            deletedCount += 1;
            cursor.continue();
          } else {
            tx.oncomplete = () => resolve(deletedCount);
          }
        } else {
          tx.oncomplete = () => resolve(deletedCount);
        }
      };

      req.onerror = e => reject((e.target as IDBRequest).error);
    });
  }

  async saveBatch(store: string, items: Array<{ key: Key; value: unknown }>): Promise<boolean> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readwrite");
      const objectStore = tx.objectStore(store);

      items.forEach(({ key, value }) => {
        objectStore.put(value, key);
      });

      tx.oncomplete = () => resolve(true);
      tx.onerror = e => reject((e.target as IDBTransaction).error);
    });
  }

  async deleteBatch(store: string, keys: Key[]): Promise<number> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readwrite");
      const objectStore = tx.objectStore(store);
      let deletedCount = 0;

      keys.forEach(key => {
        const req = objectStore.delete(key);
        req.onsuccess = () => {
          deletedCount += 1;
        };
      });

      tx.oncomplete = () => resolve(deletedCount);
      tx.onerror = e => reject((e.target as IDBTransaction).error);
    });
  }

  async getAll<T = unknown>(store: string): Promise<Array<{ key: Key; value: T }>> {
    await this.ready;
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }
      const tx = this.db.transaction(store, "readonly");
      const objectStore = tx.objectStore(store);
      const results: Array<{ key: Key; value: T }> = [];
      const req = objectStore.openCursor();

      req.onsuccess = (e: Event) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          results.push({ key: cursor.key, value: cursor.value as T });
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      req.onerror = e => reject((e.target as IDBRequest).error);
    });
  }

  async clearStore(store: string): Promise<boolean> {
    await this.ready;

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject("DB not initialized");
        return;
      }

      const tx = this.db.transaction(store, "readwrite");
      const objectStore = tx.objectStore(store);

      objectStore.clear();

      tx.oncomplete = () => resolve(true);
      tx.onerror = e => reject((e.target as IDBTransaction).error);
    });
  }
}

const db = new IndexedDB();

export { db as default, stores };
